import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { IconAccordion } from '@components/molecules/IconAccordion';
import { SelectableAddress } from '@types';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useAddDeliveryMethodToCart } from '@services/useAddDeliveryMethodToCart';
import { useData } from '@hooks/useData';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Notification } from '@components/molecules/Notification';
import { AccordionRef } from '@components/atoms/Accordion';
import useCartSummary from '@hooks/useCartSummary';
import AtAddress from './_subcomponents/atAddress/AtAddress';
import AtStorePickup from './_subcomponents/atStorePickup/AtStorePickup';
import PickupSpotsForm from './_subcomponents/pickupSpotsForm/PickupSpotsForm';
import AtLocker from './_subcomponents/atLocker/AtLocker';

const CURRENT_STEP = 3;

export const DeliveryAddress: FC = () => {
    const { navigate } = useCustomNavigate();
    const { login, allSettings, cart, addresses, allGeoDistricts } = useData();
    const { request: requestAddDeliveryMethodToCart, lastResponseTime } = useAddDeliveryMethodToCart();
    const { formattedCart } = useCartSummary(cart?.data);
    const { t } = useTranslation();

    const translations = {
        title: t('delivery-address-component.title'),
        addressTitle: t('delivery-address-component.address.title'),
        addressDescription: t('delivery-address-component.address.description'),
        storeTitle: t('delivery-address-component.store.title'),
        storeDescription: t('delivery-address-component.store.description'),
        pickupSpotTitle: t('delivery-address-component.pickup-spot.title'),
        pickupSpotDescription: t('delivery-address-component.pickup-spot.description'),
        lockerTitle: t('delivery-address-component.locker.title'),
        lockerDescription: t('delivery-address-component.locker.description'),
        continueButton: t('delivery-address-component.continue.button'),
        requiredMinOrderPrice: t('shopping-cart-general-messages.required-min-order-price', {
            price: cart?.data?.paymentRequirements?.minOrderPrice,
        }),
    };

    useEffect(() => {
        translations.requiredMinOrderPrice = t('shopping-cart-general-messages.required-min-order-price', {
            price: cart?.data?.paymentRequirements?.minOrderPrice,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.paymentRequirements?.minOrderPrice]);

    const canDeliverToHomeAddress = allSettings?.data?.shippingMethod === 'CTT';
    const canDeliverToPickupSpot = allSettings?.data?.shippingMethod === 'DPD';
    const canDeliverToLocker = allSettings?.data?.module_locky?.active;

    const deliveryAddresses =
        (addresses?.data || [])
            .filter((item) => item.type === 'delivery')
            .map((item, key) => {
                const matchingDistrict = Object.values(allGeoDistricts?.data || {}).find(
                    (district) => district.district_code === item.district
                );

                return {
                    value: 'address-' + key,
                    uuid: item.uuid || '',
                    name: item.name || '',
                    lastName: item.lastName || '',
                    fiscalNumber: item.fiscalNumber || '',
                    address: `${item.address || ''} ${item.complement || ''}`.trim(),
                    postalCode: `${item.postalCode || ''} ${matchingDistrict?.name || ''}`.trim(),
                    region: `${item.region || ''}`,
                    initiallySelected: !!item.primary,
                };
            }) ?? [];

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    const accordionRef = useRef<AccordionRef>(null);

    // tab initially open
    const [initialOpenTab, setInitialOpenTab] = useState<'address' | 'at-store' | 'pickup-spot' | 'locker' | null>(null);

    useEffect(() => {
        if (allSettings?.data?.shippingMethod && initialOpenTab === null) {
            if (cart?.data?.delivery?.shipToPickupSpotId !== undefined && cart?.data?.delivery?.shipToPickupSpotId !== null) {
                setInitialOpenTab('pickup-spot');
            } else if (cart?.data?.delivery?.pickupAtStore) {
                setInitialOpenTab('at-store');
            } else if (cart?.data?.delivery?.shipToAddress) {
                setInitialOpenTab('address');
            } else if (cart?.data?.delivery?.pickupAtLocker) {
                setInitialOpenTab('locker');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cart?.data?.delivery?.shipToAddress,
        cart?.data?.delivery?.pickupAtStore,
        cart?.data?.delivery?.shipToPickupSpotId,
        allSettings?.data?.shippingMethod,
    ]);

    useEffect(() => {
        if (initialOpenTab) {
            accordionRef.current?.open(initialOpenTab);
        }
    }, [initialOpenTab]);

    /* shipping to home address */

    const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
    const [addressType, setAddressType] = useState<'old' | 'new'>('old');

    const handleOnChangeAddress = (id: string) => setSelectedAddress(id);

    const [lastAddedAddress, setLastAddedAddress] = useState<SelectableAddress | null>(null);

    /* at store pickup */

    const [selectedAtStorePickup, setSelectedAtStorePickup] = useState<boolean>(false);

    const handleOnSelectAtStorePickup = () => setSelectedAtStorePickup(true);

    /* shipping to pickup spot */

    const [selectedPickupSpot, setSelectedPickupSpot] = useState<string | null>(null);

    const handleOnChangePickupSpot = (value: string | null) => setSelectedPickupSpot(value);

    /* at locker pickup */

    const [selectedLocker, setSelectedLocker] = useState<string | null>(null);

    const handleOnSelectLocker = (value: string | null) => setSelectedLocker(value);

    // check if order has min price
    const [hasMinOrderPrice, setHasMinOrderPrice] = useState<false | null>(null);

    // may the user proceed to next page?
    const isButtonEnabled =
        // has address selected
        selectedAddress !== null ||
        // has at store pickup selected
        cart?.data?.delivery?.pickupAtStore ||
        selectedAtStorePickup ||
        // has pickup spot selected
        cart?.data?.delivery?.shipToPickupSpotId ||
        selectedPickupSpot !== null ||
        // has locker selected
        cart?.data?.delivery?.pickupAtLocker ||
        selectedLocker !== null;

    useEffect(() => {
        // unselect other delivery types
        if ((addressType === 'old' && lastAddedAddress) || selectedAddress) {
            setSelectedAtStorePickup(false);
            setSelectedPickupSpot(null);
            setSelectedLocker(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressType, selectedAddress]);

    // when user selects at store pickup
    useEffect(() => {
        // unselect other delivery types
        if (selectedAtStorePickup) {
            setSelectedAddress(null);
            setSelectedPickupSpot(null);
            setSelectedLocker(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAtStorePickup]);

    // when user selects a pickup spot
    useEffect(() => {
        // unselect other delivery types
        if (selectedPickupSpot) {
            setSelectedAddress(null);
            setSelectedAtStorePickup(false);
            setSelectedLocker(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPickupSpot]);

    // when user selects a locker
    useEffect(() => {
        // unselect other delivery types
        if (selectedLocker) {
            setSelectedAddress(null);
            setSelectedPickupSpot(null);
            setSelectedAtStorePickup(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocker]);

    useEffect(() => {
        if (addressType === 'new' && !lastAddedAddress) {
            const lastAddress = deliveryAddresses[deliveryAddresses.length - 1];
            if (lastAddress) {
                setLastAddedAddress(lastAddress);
                handleOnChangeAddress(lastAddress.value);
            }
            setAddressType('old');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryAddresses.length]);

    const handleSaveDelivery = () => {
        if (selectedAddress) {
            let selectedUUID: string | undefined = undefined;
            const selectedItem = deliveryAddresses.find((item) => item.value === selectedAddress);
            selectedUUID = selectedItem ? selectedItem.uuid : undefined;

            requestAddDeliveryMethodToCart({ address_uuid: selectedUUID });
        } else if (selectedAtStorePickup) {
            requestAddDeliveryMethodToCart({ at_store_pickup: true });
        } else if (selectedPickupSpot) {
            requestAddDeliveryMethodToCart({ pickup_spot_id: selectedPickupSpot || '' });
        } else if (selectedLocker) {
            requestAddDeliveryMethodToCart({ locker_id: selectedLocker || '' });
        } else {
            // changed nothing, go to next step
            navigate(`/${SITE_URLS.orderPayment}`);
        }
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (!cart?.data?.paymentRequirements?.hasMinOrderPrice) {
                setHasMinOrderPrice(false);
            } else {
                navigate(`/${SITE_URLS.orderPayment}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const formattedTotal = formattedCart();

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        type="button"
                        label={translations.continueButton}
                        onClick={handleSaveDelivery}
                        mode={ButtonMode.primary}
                        disabled={!isButtonEnabled}
                    />
                </div>
            }
            total={formattedTotal}>
            <>
                {hasMinOrderPrice === false && (
                    <Notification
                        type="error"
                        message={translations.requiredMinOrderPrice}
                        borderRadiusStyle="rounded"
                        size="full-width"
                    />
                )}

                <IconAccordion
                    accordionRef={accordionRef}
                    id="shopping-accordion"
                    items={[
                        canDeliverToHomeAddress
                            ? {
                                  id: 'address',
                                  title: translations.addressTitle,
                                  description: translations.addressDescription,
                                  icon: 'fa-thin fa-house',
                                  content: (
                                      <AtAddress
                                          addresses={deliveryAddresses}
                                          selectedAddress={selectedAddress}
                                          lastAddedAddress={lastAddedAddress}
                                          handleOnChangeAddress={handleOnChangeAddress}
                                          addressType={addressType}
                                          setAddressType={setAddressType}
                                      />
                                  ),
                              }
                            : undefined,
                        {
                            id: 'at-store',
                            title: translations.storeTitle,
                            description: translations.storeDescription,
                            icon: 'fa-thin fa-store',
                            content: (
                                <AtStorePickup
                                    selectedAtStorePickup={selectedAtStorePickup}
                                    handleOnSelectAtStorePickup={handleOnSelectAtStorePickup}
                                />
                            ),
                        },
                        canDeliverToPickupSpot
                            ? {
                                  id: 'pickup-spot',
                                  title: translations.pickupSpotTitle,
                                  description: translations.pickupSpotDescription,
                                  icon: 'fa-thin fa-location-dot',
                                  content: (
                                      <PickupSpotsForm
                                          selectedPickupSpot={selectedPickupSpot}
                                          handleOnChangePickupSpot={handleOnChangePickupSpot}
                                          selectedAtStore={selectedAtStorePickup}
                                          selectedAddress={selectedAddress !== null}
                                      />
                                  ),
                              }
                            : undefined,
                        canDeliverToLocker
                            ? {
                                  id: 'locker',
                                  title: translations.lockerTitle,
                                  description: translations.lockerDescription,
                                  icon: 'fa-thin fa-window-frame',
                                  content: (
                                      <AtLocker selectedLocker={selectedLocker} handleOnSelectLocker={handleOnSelectLocker} />
                                  ),
                              }
                            : undefined,
                    ].filter((item) => item !== undefined)}
                />
            </>
        </ShoppingCartContainer>
    );
};
